// Play a card's video because it is ON SCREEN, not because the cursor is resting on it.
//
// `useHoverPreview` is the right rule for a row of twenty small cards: a sweep across them
// should cost nothing, so nothing loads until you deliberately stop. The big feature blocks
// between Explore's rows are the opposite case — there are one or two of them on the whole
// page, each the size of a quarter of the screen, and a still that large reads as a dead
// slab next to a hero that moves. They should be alive when you reach them.
//
// So: mount and play when the block is genuinely in view, pause and unmount when it leaves.
// Same two guarantees as the hover version and for the same reasons — the element exists
// only while it is wanted, and it fades in on `canplay` rather than appearing as a black
// rectangle over the still.
import React, { useCallback, useEffect, useRef, useState } from "react";

/** Most of the block has to be on screen, not a corner of it. A block creeping past the
 *  bottom edge while you scroll to something else is not something anyone is watching. */
const VISIBLE_FRACTION = 0.55;

/** Long enough that scrolling THROUGH a block does not start it. The hover version waits
 *  500ms for the same reason; this is the scroll equivalent. */
const SETTLE_MS = 400;

export function useAmbientPreview(downloadUrl, className) {
  const hostRef = useRef(null);
  const videoRef = useRef(null);
  const timerRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [ready, setReady] = useState(false);

  const stop = useCallback(() => {
    clearTimeout(timerRef.current);
    setPlaying(false);
    setReady(false);
  }, []);

  useEffect(() => {
    const el = hostRef.current;
    if (!el || !downloadUrl) return undefined;

    // Someone who has asked for less motion gets the still. This is decoration; it is the
    // first thing that should go.
    const motionOk = !window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (!motionOk || typeof IntersectionObserver === "undefined") return undefined;

    const io = new IntersectionObserver(
      ([entry]) => {
        clearTimeout(timerRef.current);
        if (entry.isIntersecting) {
          timerRef.current = setTimeout(() => setPlaying(true), SETTLE_MS);
        } else {
          setPlaying(false);
          setReady(false);
        }
      },
      { threshold: VISIBLE_FRACTION },
    );
    io.observe(el);
    return () => {
      io.disconnect();
      clearTimeout(timerRef.current);
    };
  }, [downloadUrl]);

  // A tab in the background should not be decoding video nobody can see.
  useEffect(() => {
    const onVis = () => { if (document.hidden) stop(); };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [stop]);

  const preview = playing ? (
    <video
      ref={videoRef}
      src={downloadUrl}
      muted
      loop
      autoPlay
      playsInline
      preload="none"
      onCanPlay={() => {
        setReady(true);
        videoRef.current?.play().catch(() => {});
      }}
      className={className}
      style={{ opacity: ready ? 1 : 0 }}
    />
  ) : null;

  return { hostRef, preview, ready };
}
